import React from "react";

const TermsAndConditions = () => {
  return (
    <div className="max-w-5xl mx-auto py-8 sm:py-12 px-3 sm:px-6">
      {/* Card */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 bg-gradient-to-r from-[var(--brand-primary-100)] via-[var(--brand-primary-50)] to-white">
          {/* Left - Title */}
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-12 sm:w-14 h-12 sm:h-14 rounded-lg bg-[var(--brand-primary-300)]/20 ring-1 ring-[var(--brand-primary-300)]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6 sm:w-7 sm:h-7 text-[var(--brand-primary-600)]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </div>

            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
                Terms & Conditions
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                AavryaaFabHub — Clear, fair, and transparent terms for every fabric order.
              </p>
            </div>
          </div>

          {/* Right - Buttons */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
            <a
              href="mailto:aavryaafabhub@gmail.com"
              className="px-3 sm:px-4 py-2 rounded-lg bg-[var(--brand-primary-300)]/20 text-[var(--brand-primary-700)] ring-1 ring-[var(--brand-primary-100)] hover:bg-[var(--brand-primary-300)]/30 text-sm sm:text-base"
            >
              Email Us
            </a>
            <a
              href="https://wa.me/919789389489"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 sm:px-4 py-2 rounded-lg bg-[var(--brand-primary-600)] text-white hover:opacity-95 text-sm sm:text-base"
            >
              WhatsApp
            </a>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 sm:p-8 text-gray-600 text-sm sm:text-base">
          <p className="mb-4">
            These Terms & Conditions govern all purchases and usage of products and services provided by <strong>AavryaaFabHub</strong>. 
            By accessing our website or placing an order, you agree to comply with these terms.
          </p>

          {/* Sections */}
          {[
            {
              title: "1. Definitions",
              content: [
                "“We”, “Our”, “Us” refer to AavryaaFabHub.",
                "“You”, “Your” refers to the customer purchasing from us.",
                "“Products” means all fabrics, materials, and accessories sold on our website."
              ]
            },
            {
              title: "2. Ordering",
              type: "ol",
              content: [
                "All orders are subject to availability and acceptance.",
                "Colors and patterns may vary slightly due to monitor display settings.",
                "Custom-cut fabrics are final sale and cannot be returned."
              ]
            },
            {
              title: "3. Payment",
              content: [
                "Payments must be made in full before dispatch.",
                "We accept UPI, bank transfers, credit/debit cards, and select digital wallets.",
                "All prices include applicable taxes unless stated otherwise."
              ]
            },
            {
              title: "4. Shipping",
              content: [
                "We ship across India and to select international locations.",
                "Dispatch times vary depending on product availability.",
                "Delivery delays caused by courier services are beyond our control."
              ]
            },
            {
              title: "5. Returns & Refunds",
              paragraph: [
                "Our Refund Policy applies to eligible returns. Fabrics must be unused, unwashed, and in original condition.",
                "Custom orders, clearance items, and fabrics cut to customer specifications are non-returnable."
              ]
            },
            {
              title: "6. Intellectual Property",
              content: [
                "All designs, product images, and content on our site are owned by AavryaaFabHub.",
                "Reproduction or resale without permission is strictly prohibited."
              ]
            },
            {
              title: "7. Limitation of Liability",
              content: [
                "We are not liable for indirect, incidental, or consequential damages.",
                "Our liability is limited to the value of the product purchased."
              ]
            },
            {
              title: "8. Governing Law",
              paragraph: [
                "These terms shall be governed by and construed under the laws of India.",
                "Any disputes shall be subject to the exclusive jurisdiction of the courts in Tamil Nadu."
              ]
            }
          ].map((sec, idx) => (
            <section className="mb-6" key={idx}>
              <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
                {sec.title}
              </h2>
              {sec.paragraph ? (
                sec.paragraph.map((p, i) => (
                  <p key={i} className="mb-2">
                    {p}
                  </p>
                ))
              ) : sec.type === "ol" ? (
                <ol className="list-decimal pl-6 space-y-2">
                  {sec.content.map((item, i) => <li key={i}>{item}</li>)}
                </ol>
              ) : (
                <ul className="list-disc pl-6 space-y-2">
                  {sec.content.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              )}
            </section>
          ))}

          {/* CTA */}
          <div className="mt-4 border-t pt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-xs sm:text-sm">Need clarification? Reach out to our support team:</p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <a
                  href="tel:+919789389489"
                  className="text-gray-800 font-medium hover:underline"
                >
                  📞 +91 97893 89489
                </a>
                <a
                  href="mailto:aavryaafabhub@gmail.com"
                  className="text-[var(--brand-primary-700)] font-medium hover:underline"
                >
                  ✉️ aavryaafabhub@gmail.com
                </a>
              </div>
            </div>

            <a
              href="https://wa.me/919789389489?text=Hi%20I%20have%20a%20question%20about%20the%20terms"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-lg px-4 py-2 bg-[var(--brand-primary-600)] text-white shadow-sm hover:bg-[var(--brand-primary-700)]"
            >
              Chat on WhatsApp
            </a>
          </div>

          <p className="text-xs sm:text-sm text-gray-400 mt-4">
            📅 Last Updated: August 11, 2025
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
